/**
 * Web implementation of push notifications using Firebase Cloud Messaging.
 *
 * Setup instructions:
 * https://cvb-dev.visualstudio.com/Mobile%20Team/_wiki/wikis/Mobile-Team.wiki/1025/React-Native-Web-Setup-Push-Notifications
 *
 * Prerequisites:
 * - A Firebase web app config with messaging enabled.
 * - A VAPID key from Firebase Console > Project Settings > Cloud Messaging > Web Push Certificates.
 * - A service worker registered at `/firebase-messaging-sw.js` in the public root of your app.
 *
 * Required packages:
 * - firebase (web SDK)
 */

import { useEffect } from "react";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

/**
 * Deletes an IndexedDB database by name.
 * Used to recover from Firebase messaging DB version conflicts.
 *
 * @param {string} name - The database name to delete.
 * @returns {Promise<void>}
 */
const deleteDatabase = (name) => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    request.onblocked = () => resolve();
  });
};

/**
 * Sets up web push notifications via Firebase Cloud Messaging.
 *
 * Requests notification permission from the browser, registers a service worker,
 * retrieves an FCM token, and listens for foreground messages.
 *
 * If a `VersionError` occurs (stale IndexedDB from a previous FCM setup),
 * the database is cleared and token retrieval is retried once.
 *
 * @param {import("firebase/app").FirebaseApp} app - The initialized Firebase app instance.
 * @param {string} vapidKey - The VAPID key from Firebase Console for web push.
 * @returns {Promise<string|null>} The FCM registration token, or null if permission was denied or token unavailable.
 */
export const setupPushNotifications = async (app, vapidKey) => {
  // Only Chrome supports programmatic notification permission requests without a user gesture
  const isChrome = /Chrome/.test(navigator.userAgent) && !/Edg|OPR/.test(navigator.userAgent);
  if (!isChrome) {
    console.warn("Push notifications are only supported in Chrome.");
    return null;
  }

  const permission = await Notification.requestPermission();

  if (permission !== "granted") {
    console.warn("Notification permission denied");
    return null;
  }

  const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  await navigator.serviceWorker.ready;
  const messaging = getMessaging(app);
  let currentToken = null;

  try {
    currentToken = await getToken(messaging, {
      vapidKey: vapidKey,
      serviceWorkerRegistration: registration,
    });

    if (currentToken) {
      onMessage(messaging, (payload) => {
        console.log("Foreground push:", payload.data);
        // Will be updated once we have a design for in-app notifications.
      });
    } else {
      console.warn("No registration token available.");
    }
  } catch (err) {
    // Handle IndexedDB version conflict by clearing stale database and retrying
    if (err.name === "VersionError") {
      console.warn("Firebase messaging DB version conflict. Clearing and retrying...");
      await deleteDatabase("firebase-messaging-database");
      currentToken = await getToken(messaging, {
        vapidKey: vapidKey,
        serviceWorkerRegistration: registration,
      });
    } else {
      console.warn("An error occurred while retrieving token.", err);
    }
  }

  return currentToken;
};

/**
 * Subscribes a device token to a web push notification topic.
 * Appends the environment suffix to the channel name for non-prod environments.
 *
 * @param {string} channelName - The base topic/channel name (e.g. "vbconnect").
 * @param {string} token - The FCM registration token.
 * @param {Function} subscribeToWebPushNotifications - The API call function to subscribe the token.
 * @returns {Promise<{success: boolean}|null>} The subscription response, or null on failure.
 */
export const subscribeToPushNotifications = async (channelName, token, subscribeToWebPushNotifications) => {
  let channel = channelName;
  if (process.env.REACT_APP_ENV !== "prod") {
    channel = `${channel}-${process.env.REACT_APP_ENV}`;
  }
  const response = await subscribeToWebPushNotifications(channel, token);
  return response;
};

/**
 * Unsubscribes a device token from a web push notification topic.
 * Appends the environment suffix to the channel name for non-prod environments.
 *
 * @param {string} channelName - The base topic/channel name (e.g. "vbconnect").
 * @param {string} token - The FCM registration token.
 * @param {Function} unsubscribeFromWebPushNotifications - The API call function to unsubscribe the token.
 * @returns {Promise<{success: boolean}|null>} The unsubscription response, or null on failure.
 */
export const unsubscribeFromPushNotifications = async (channelName, token, unsubscribeFromWebPushNotifications) => {
  let channel = channelName;
  if (process.env.REACT_APP_ENV !== "prod") {
    channel = `${channel}-${process.env.REACT_APP_ENV}`;
  }
  const response = await unsubscribeFromWebPushNotifications(channel, token);
  return response;
};

/**
 * getApiKey,
 * setFirebaseVapidKey,
 * firebaseVapidKey,
 * setNotificationToken,
 * notificationToken,
 * subscribeToWebPushNotifications
 * setNotificationSubscription
 */
export const useInitPushNotifications = (platform, channelName, firebaseInit = {}) => {
  useEffect(() => {
    const fetchFirebaseVapidKey = async () => {
      if (platform === "web") {
        if (firebaseInit.firebaseVapidKey === null) {
          const apiKeyResponse = await firebaseInit.getApiKey("vapid");
          firebaseInit.setFirebaseVapidKey(apiKeyResponse);
        }
      }
    };

    fetchFirebaseVapidKey();
  }, [platform, firebaseInit]);

  useEffect(() => {
    if (platform !== "web" || firebaseInit.notificationToken) return;
    if (!firebaseInit.firebaseVapidKey) return;
    if (!firebaseInit.app) return;
    if (Notification.permission === "denied") return;

    const init = async () => {
      try {
        const fcmToken = await setupPushNotifications(firebaseInit.app, firebaseInit.firebaseVapidKey);
        if (fcmToken) {
          firebaseInit.setNotificationToken(fcmToken);
        }
      } catch (error) {
        console.warn("Failed to set up push notifications:", error);
      }
    };

    // Delay initialization to ensure Firebase container is ready
    if (document.readyState === "complete") {
      init();
    } else {
      window.addEventListener("load", init);
      return () => window.removeEventListener("load", init);
    }
  }, [platform, firebaseInit]);

  useEffect(() => {
    if (!firebaseInit.notificationToken || platform !== "web") return;

    (async () => {
      try {
        const response = await subscribeToPushNotifications(
          channelName,
          firebaseInit.notificationToken,
          firebaseInit.subscribeToWebPushNotifications
        );

        if (response) {
          firebaseInit.setNotificationSubscription(response.success);
        }
      } catch (error) {
        console.warn("Failed to subscribe to web push notifications:", error);
      }
    })();
  }, [platform, channelName, firebaseInit]);
};

export async function checkNotificationPermission() {
  return Notification.permission === "granted";
}
